import React from "react";
import AutoComplete from "./autoComplete";
import Datepicker from "./datepicker";
import Input from "./textInput";
import Select from "./select";
import Switch from "./switch";
import {
  FormInstance as AntFormInstance,
  Form as AntForm,
  FormProps,
  Checkbox,
} from "antd";

interface FormInstance extends AntFormInstance {
  uuid: string;
  extras?: Field[];
  initialValues?: {};
  isFieldFocused: (name: string) => boolean;
  onFieldFocus: (name: string) => void;
}

interface Field {
  name: string | (string | number)[];
  value?: any;
  label?: string;
  rules?: any[];
  valuePropName?: string;
  face?: string | "checkbox" | "select";
  [key: string]: any;
}

// Extend the Ant Design FormProps
interface Props extends Omit<FormProps, "fields"> {
  fields: (Field | Field[])[];
  form: FormInstance;
  children?: React.ReactNode;
  styles?: { field?: React.CSSProperties };
  renderFormItem?: (field: Field) => React.ReactNode;
}

const Item = ({
  face = "",
  label,
  ...props
}: {
  face?: string;
  label?: string;
  [key: string]: any;
}) => {
  if (face === "autocomplete") return <AutoComplete {...props} label={label} />;
  if (face === "select") return <Select {...props} label={label} />;
  if (face === "datepicker") return <Datepicker {...props} label={label} />;
  if (face === "checkbox") return <Checkbox {...props} />;
  if (face === "switch")
    return (
      <span className="inline-flex items-center gap-[1rem]">
        <Switch {...props} />
        {!!label && <span>{label}</span>}
      </span>
    );
  return <Input {...props} label={label} />;
};

const Form = ({
  form,
  styles,
  children,
  fields = [],
  preserve = false,
  renderFormItem = () => null,
  ...props
}: Props) => {
  const renderField = (item: Field, className: string) => {
    const { label, name, rules, valuePropName, face = "", ...itemProps } = item;
    return (
      renderFormItem(item) ?? (
        <AntForm.Item
          name={name}
          valuePropName={valuePropName}
          className={className}
          rules={rules?.map((rule) => ({
            ...rule,
            message:
              rule.message ||
              (!!rule.required && `Please, enter ${label}`) ||
              (rule.type === "email" && `Please, enter valid email`),
          }))}
        >
          <Item
            {...itemProps}
            face={face}
            label={label}
            rules={rules}
            style={styles?.field}
            onFocus={() =>
              form.onFieldFocus(Array.isArray(name) ? name.join(".") : name)
            }
            onChange={(a: any, b: any) => {
              if (!!itemProps.onChange) itemProps.onChange(a, b);
              if (!!b?.values && !!form) form.setFieldsValue(b?.values);
            }}
          />
        </AntForm.Item>
      )
    );
  };

  const renderItem = (item: Field | Field[], index: number) => {
    const top = `m-0${index ? " m-20-t" : ""}`;
    if (Array.isArray(item))
      return (
        <div className={`${top} flex gap-[2rem] mobile:flex-col`}>
          {item.map((field) => renderField(field, "m-0 min-w-0 flex-1"))}
        </div>
      );
    return renderField(item, `w-100 ${top}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    const button = document.getElementById(form.uuid)?.querySelector("button");
    if (
      !!button &&
      e.key === "Enter" &&
      form.isFieldFocused(
        [fields.flat().at(-1)?.name].flat().filter(Boolean).join("."),
      )
    )
      button.click();
  };

  return (
    <AntForm
      form={form}
      id={form.uuid}
      layout="vertical"
      autoComplete="on"
      preserve={preserve}
      onKeyDown={handleKeyDown}
      initialValues={form.initialValues}
      {...props}
    >
      {React.Children.toArray(fields.map(renderItem))}
      {React.Children.toArray(
        form.extras?.map((extra) => (
          <AntForm.Item className="d-none" {...extra} />
        )),
      )}
      {children}
    </AntForm>
  );
};

Form.useForm = AntForm.useForm;
Form.useWatch = AntForm.useWatch;

export default Form;
